import { useCallback, useEffect, useRef, useState } from 'react';

export const hashContents = (contents) => {
  if (!Array.isArray(contents)) return '';
  return contents
    .filter(Boolean)
    .map((c) => {
      const id = c.id || '';
      const def = c.definition || '';
      const x = c.GeometryX ?? '';
      const y = c.GeometryY ?? '';
      const w = c.GeometryWidth ?? '';
      const h = c.GeometryHeight ?? '';
      const src = c.source || '';
      const tgt = c.target || '';
      const style = c.style || '';
      const pts = c.GeometryPoints || '';
      const kosul = c.kosul || '';
      return `${id}|${def}|${x}|${y}|${w}|${h}|${src}|${tgt}|${style}|${pts}|${kosul}`;
    })
    .sort()
    .join('\n');
};

const useTabManager = ({
  workflowData,
  navigate,
  fetchWrapper,
  selectedShape,
  setSelectedShape,
  setCode,
  editorRef,
  isUpdatingEditorRef,
  shapeCodeCacheRef,
  previousSelectedShapeRef,
  previousActiveTabIdRef,
  graphRef,
  onBeforeTabLeave,
}) => {
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [tabCounter, setTabCounter] = useState(0);
  const tabCodeCacheRef = useRef({});
  const workflowDataRef = useRef(workflowData);
  const selectedShapePerTabRef = useRef({});
  const originalScriptsRef = useRef({});
  const savedWorkflowHashRef = useRef({});

  useEffect(() => {
    const activeTab = tabs.find((tab) => tab.id === activeTabId);
    if (activeTab?.workflowData) {
      workflowDataRef.current = activeTab.workflowData;
    } else if (workflowData) {
      workflowDataRef.current = workflowData;
    }
  }, [activeTabId, tabs, workflowData]);

  useEffect(() => {
    if (workflowData && tabs.length === 0 && !activeTabId) {
      const initialTabId = `tab-${tabCounter}`;
      const diagrunId = workflowData?.workflow?.id || null;
      const initialTab = {
        id: initialTabId,
        name: workflowData?.workflow?.filename || workflowData?.workflow?.desc || 'Flowchart',
        fileId: diagrunId ? String(diagrunId) : null,
        file: null,
        workflowData: workflowData,
      };
      setTabs([initialTab]);
      setActiveTabId(initialTabId);
      setTabCounter((prev) => prev + 1);
      if (!tabCodeCacheRef.current[initialTabId]) {
        tabCodeCacheRef.current[initialTabId] = {};
      }
      if (workflowData?.contents && !originalScriptsRef.current[initialTabId]) {
        const snap = {};
        workflowData.contents.forEach((item) => {
          if (item && item.id) {
            snap[String(item.id)] = item.script != null ? String(item.script) : '';
          }
        });
        originalScriptsRef.current[initialTabId] = snap;
      }
      if (workflowData?.contents) {
        savedWorkflowHashRef.current[initialTabId] = hashContents(workflowData.contents);
      }
    }
  }, [workflowData, tabs.length, activeTabId, tabCounter]);

  const flushEditorToCache = useCallback(() => {
    const currentTabId = activeTabId;
    const currentShape = selectedShape;
    if (!currentTabId || !currentShape) return;

    if (!tabCodeCacheRef.current[currentTabId]) {
      tabCodeCacheRef.current[currentTabId] = {};
    }

    const existingCacheValue =
      tabCodeCacheRef.current[currentTabId][currentShape] !== undefined
        ? tabCodeCacheRef.current[currentTabId][currentShape]
        : null;

    let valueToCache = '';
    if (editorRef.current) {
      try {
        const currentEditorValue = editorRef.current.getValue();
        const editorValue =
          currentEditorValue !== undefined && currentEditorValue !== null ? String(currentEditorValue) : '';

        if (editorValue === '' && existingCacheValue !== null && existingCacheValue !== undefined && existingCacheValue !== '') {
          valueToCache = String(existingCacheValue);
        } else {
          valueToCache = editorValue;
        }
      } catch (error) {
        if (existingCacheValue !== null && existingCacheValue !== undefined) {
          valueToCache = String(existingCacheValue);
        }
      }
    } else if (existingCacheValue !== null && existingCacheValue !== undefined) {
      valueToCache = String(existingCacheValue);
    }

    tabCodeCacheRef.current[currentTabId][currentShape] = valueToCache;
    shapeCodeCacheRef.current[currentShape] = valueToCache;
  }, [activeTabId, selectedShape, editorRef, shapeCodeCacheRef]);

  const snapshotGraphToActiveTab = useCallback(() => {
    const currentTabId = activeTabId;
    if (!currentTabId || !graphRef?.current) {
      console.warn('[Snapshot] Atlandı — graphRef veya activeTabId yok', { currentTabId, hasGraph: !!graphRef?.current });
      return;
    }
    const graph = graphRef.current;
    const model = graph.getModel();
    const cells = graph.getChildCells(graph.getDefaultParent());
    if (!cells || cells.length === 0) {
      console.warn('[Snapshot] Atlandı — graph cells boş');
      return;
    }
    console.log(`%c[Snapshot] ${currentTabId} — ${cells.length} cell kaydediliyor`, 'color:#4ade80;font-weight:bold');

    const snap = cells.map((c) => {
      const iv = model.isVertex(c);
      const g = c.getGeometry();
      const r = {
        id: c.getId() ? String(c.getId()) : '',
        vertex: iv ? '1' : '0',
        edge: !iv ? '1' : '0',
        definition: model.getValue(c) ? String(model.getValue(c)) : '',
        GeometryX: g.x, GeometryY: g.y, GeometryWidth: g.width, GeometryHeight: g.height,
        source: !iv && c.getTerminal(true) ? String(c.getTerminal(true).getId()) : null,
        target: !iv && c.getTerminal(false) ? String(c.getTerminal(false).getId()) : null,
        style: c.getStyle() ? String(c.getStyle()) : '',
      };
      if (!iv) {
        const lbl = r.definition;
        if (lbl && lbl.includes(':Next')) { const km = lbl.match(/(\d+):Next/); if (km) r.kosul = km[1]; }
        if (c.getGeometry().points) {
          try { r.GeometryPoints = JSON.stringify(c.getGeometry().points.map((p) => ({ x: p.x, y: p.y }))); } catch (_) { /* */ }
        }
      }
      return r;
    });

    const cache = tabCodeCacheRef.current[currentTabId] || {};

    setTabs((prev) =>
      prev.map((t) => {
        if (t.id !== currentTabId) return t;
        const pWd = t.workflowData || workflowDataRef.current;
        if (!pWd) return t;
        const oc = pWd.contents || [];
        const merged = snap.map((s) => {
          const o = oc.find((x) => String(x.id) === String(s.id));
          const cachedScript = cache[s.id] !== undefined ? String(cache[s.id]) : (o?.script || '');
          const base = o ? { ...o, ...s, script: cachedScript, ai_prompt: o.ai_prompt, include: o.include } : { ...s, script: cachedScript };
          return base;
        });
        return { ...t, workflowData: { ...pWd, contents: merged } };
      }),
    );
  }, [activeTabId, graphRef]);

  const handleFileClick = useCallback(
    async (file) => {
      if (!file.diagrun_id) {
        return;
      }

      const targetId = String(file.diagrun_id);
      const existingTab = tabs.find((tab) =>
        (tab.fileId && String(tab.fileId) === targetId) ||
        (tab.workflowData?.workflow?.id && String(tab.workflowData.workflow.id) === targetId)
      );
      
      if (existingTab) {
        if (existingTab.fileId == null && targetId) {
          setTabs((prev) => prev.map((t) =>
            t.id === existingTab.id ? { ...t, fileId: targetId } : t
          ));
        }
        if (existingTab.id === activeTabId) return;

        if (onBeforeTabLeave) onBeforeTabLeave();
        flushEditorToCache();

        if (activeTabId && selectedShape) {
          selectedShapePerTabRef.current[activeTabId] = selectedShape;
        }

        previousSelectedShapeRef.current = selectedShape;
        previousActiveTabIdRef.current = activeTabId;
        setActiveTabId(existingTab.id);

        const restoredShape = selectedShapePerTabRef.current[existingTab.id] || null;
        setSelectedShape(restoredShape);

        if (restoredShape && tabCodeCacheRef.current[existingTab.id]) {
          const cachedCode = tabCodeCacheRef.current[existingTab.id][restoredShape];
          const codeValue = cachedCode !== undefined && cachedCode !== null ? String(cachedCode) : '';
          setCode(codeValue);
          if (editorRef.current) {
            isUpdatingEditorRef.current = true;
            editorRef.current.setValue(codeValue);
            setTimeout(() => { isUpdatingEditorRef.current = false; }, 150);
          }
        } else {
          setCode('');
          if (editorRef.current) {
            isUpdatingEditorRef.current = true;
            editorRef.current.setValue('');
            setTimeout(() => { isUpdatingEditorRef.current = false; }, 100);
          }
        }
        return;
      }

      if (onBeforeTabLeave) onBeforeTabLeave();
      flushEditorToCache();

      if (activeTabId && selectedShape) {
        selectedShapePerTabRef.current[activeTabId] = selectedShape;
      }

      const newTabId = `tab-${tabCounter}`;
      const newTab = {
        id: newTabId,
        name: file.name || 'New Tab',
        fileId: file.diagrun_id,
        file: file,
        workflowData: null,
        isLoading: true,
      };

      setTabs((prev) => [...prev, newTab]);
      setTabCounter((prev) => prev + 1);

      if (!tabCodeCacheRef.current[newTabId]) {
        tabCodeCacheRef.current[newTabId] = {};
      }

      previousSelectedShapeRef.current = selectedShape;
      previousActiveTabIdRef.current = activeTabId;
      setSelectedShape(null);
      setCode('');
      if (editorRef.current) {
        isUpdatingEditorRef.current = true;
        editorRef.current.setValue('');
        setTimeout(() => { isUpdatingEditorRef.current = false; }, 100);
      }

      try {
        const processId = process.env.REACT_APP_GET_DIAGRUN;
        
        if (!processId) {
          throw new Error('REACT_APP_GET_DIAGRUN environment variable tanımlı değil!');
        }

        const bodyParams = {
          diagrun_id: file.diagrun_id,
          process: '100023232',
        };
        
        if (!fetchWrapper) {
          throw new Error('fetchWrapper undefined!');
        }
        
        const response = await fetchWrapper(processId, bodyParams, navigate);
        const workflowDataFromResponse = response?.result || response?.workflow || response;
        
        if (workflowDataFromResponse && (workflowDataFromResponse.workflow || workflowDataFromResponse.contents)) {
          setTabs((prev) =>
            prev.map((tab) => 
              tab.id === newTabId 
                ? { ...tab, workflowData: workflowDataFromResponse, isLoading: false } 
                : tab
            ),
          );
          if (workflowDataFromResponse.contents) {
            const snap = {};
            workflowDataFromResponse.contents.forEach((item) => {
              if (item && item.id) {
                snap[String(item.id)] = item.script != null ? String(item.script) : '';
              }
            });
            originalScriptsRef.current[newTabId] = snap;
            savedWorkflowHashRef.current[newTabId] = hashContents(workflowDataFromResponse.contents);
          }
          setActiveTabId(newTabId);
        } else {
          setTabs((prev) =>
            prev.map((tab) => 
              tab.id === newTabId 
                ? { ...tab, isLoading: false, error: 'Veri formatı geçersiz' } 
                : tab
            ),
          );
        }
      } catch (error) {
        console.error('File load error:', error.message);
        
        setTabs((prev) =>
          prev.map((tab) => 
            tab.id === newTabId 
              ? { ...tab, isLoading: false, error: error.message || 'Yükleme hatası' } 
              : tab
          ),
        );
      }
    },
    [tabs, tabCounter, activeTabId, selectedShape, navigate, fetchWrapper, flushEditorToCache, onBeforeTabLeave,
     setTabs, setTabCounter, setActiveTabId, setSelectedShape, setCode, editorRef, isUpdatingEditorRef,
     shapeCodeCacheRef, previousSelectedShapeRef, previousActiveTabIdRef],
  );

  const handleTabSwitch = useCallback(
    (tabId) => {
      if (tabId === activeTabId) return;

      if (onBeforeTabLeave) onBeforeTabLeave();
      flushEditorToCache();

      if (activeTabId && selectedShape) {
        selectedShapePerTabRef.current[activeTabId] = selectedShape;
      }

      previousSelectedShapeRef.current = selectedShape;
      previousActiveTabIdRef.current = activeTabId;

      setActiveTabId(tabId);

      const restoredShape = selectedShapePerTabRef.current[tabId] || null;
      setSelectedShape(restoredShape);

      if (restoredShape && tabCodeCacheRef.current[tabId]) {
        const cachedCode = tabCodeCacheRef.current[tabId][restoredShape];
        const codeValue = cachedCode !== undefined && cachedCode !== null ? String(cachedCode) : '';
        setCode(codeValue);
        if (editorRef.current) {
          isUpdatingEditorRef.current = true;
          editorRef.current.setValue(codeValue);
          setTimeout(() => { isUpdatingEditorRef.current = false; }, 150);
        }
      } else {
        setCode('');
        if (editorRef.current) {
          isUpdatingEditorRef.current = true;
          editorRef.current.setValue('');
          setTimeout(() => { isUpdatingEditorRef.current = false; }, 100);
        }
      }
    },
    [
      activeTabId,
      selectedShape,
      flushEditorToCache,
      onBeforeTabLeave,
      editorRef,
      isUpdatingEditorRef,
      previousSelectedShapeRef,
      previousActiveTabIdRef,
      setSelectedShape,
      setCode,
    ],
  );

  const handleTabClose = useCallback(
    (tabId, e) => {
      e.stopPropagation();

      const tabIndex = tabs.findIndex((t) => t.id === tabId);
      if (tabIndex === -1) return;

      if (tabs.length === 1) {
        alert('En az bir tab açık olmalıdır!');
        return;
      }

      delete selectedShapePerTabRef.current[tabId];
      delete tabCodeCacheRef.current[tabId];
      delete originalScriptsRef.current[tabId];
      delete savedWorkflowHashRef.current[tabId];

      const newTabs = tabs.filter((t) => t.id !== tabId);
      setTabs(newTabs);

      if (activeTabId === tabId) {
        const newActiveIndex = tabIndex > 0 ? tabIndex - 1 : 0;
        const newActiveTab = newTabs[newActiveIndex];
        if (newActiveTab) {
          const restoredShape = selectedShapePerTabRef.current[newActiveTab.id] || null;
          setActiveTabId(newActiveTab.id);
          setSelectedShape(restoredShape);

          if (restoredShape && tabCodeCacheRef.current[newActiveTab.id]) {
            const cachedCode = tabCodeCacheRef.current[newActiveTab.id][restoredShape];
            const codeValue = cachedCode !== undefined && cachedCode !== null ? String(cachedCode) : '';
            setCode(codeValue);
            if (editorRef.current) {
              isUpdatingEditorRef.current = true;
              editorRef.current.setValue(codeValue);
              setTimeout(() => { isUpdatingEditorRef.current = false; }, 150);
            }
          } else {
            setCode('');
            if (editorRef.current) {
              isUpdatingEditorRef.current = true;
              editorRef.current.setValue('');
              setTimeout(() => { isUpdatingEditorRef.current = false; }, 100);
            }
          }
        }
      }
    },
    [tabs, activeTabId, setSelectedShape, setCode, editorRef, isUpdatingEditorRef],
  );

  return {
    tabs,
    setTabs,
    activeTabId,
    setActiveTabId,
    tabCounter,
    setTabCounter,
    tabCodeCacheRef,
    workflowDataRef,
    selectedShapePerTabRef,
    originalScriptsRef,
    savedWorkflowHashRef,
    handleFileClick,
    handleTabSwitch,
    handleTabClose,
  };
};

export default useTabManager;
